/* eslint-disable */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Heading,
  Grid,
  GridItem,
  Input,
  Button,
  Stack,
  Text,
  Spinner,
  Alert,
  AlertIcon,
  Select,
  FormControl,
  FormLabel,
  FormErrorMessage,
} from '@chakra-ui/react';
import { fetchProviders, fetchAvailableTimeSlots } from '../../services/api';


function Step3ProviderTime({ form, setForm, showErrors = false, showVet = true }) {
  const [providers, setProviders] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  // For "no preference": maps a displayed time slot to the vet the backend assigned it.
  const [slotVetByTime, setSlotVetByTime] = useState({});
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [loadingTimeSlots, setLoadingTimeSlots] = useState(false);
  const [error, setError] = useState(null);

  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const minDate = today.toISOString().split('T')[0];
  const maxDate = new Date(today.getFullYear(), today.getMonth() + 3, today.getDate()).toISOString().split('T')[0];

  // Set default provider to "no preference" on initial load
  useEffect(() => {
    if (!form.provider) {
      setForm((f) => ({
        ...f,
        provider: { id: 'no_preference', name: 'No Preference' },
        assignedVet: null,
      }));
    }
  }, []);

  // Load providers when location changes
  useEffect(() => {
    if (form.location?.clinicId) {
      loadProviders(form.location.clinicId);
    }
  }, [form.location?.clinicId]);

  // Note: the assigned vet for "no preference" is resolved when slots are fetched
  // with a date below. We intentionally do NOT pre-fetch without a date — the
  // backend requires a date and would reject a dateless request with a 400.

  // Load time slots when date is selected (for both no_preference and specific vets)
  useEffect(() => {
    if (form.provider?.id && form.date && form.pets && form.pets.length > 0) {
      loadTimeSlots(form.provider.id, form.date);
    } else if (!form.date) {
      // Clear time slots if no date is selected, but keep assignedVet for no_preference
      setTimeSlots([]);
    }
  }, [form.provider?.id, form.date, form.pets]);

  const loadProviders = async (clinicId) => {
    setLoadingProviders(true);
    setError(null);
    try {
      const data = await fetchProviders(clinicId);
      setProviders(data || []);
    } catch (err) {
      setError('No available appointments.');
      console.error('Error loading providers:', err);
      setProviders([]);
    } finally {
      setLoadingProviders(false);
    }
  };

  const loadTimeSlots = async (providerId, date) => {
    setLoadingTimeSlots(true);
    setError(null);
    try {
      if (!form.location?.clinicId) {
        throw new Error('Clinic ID not found in selected location');
      }
      if (!form.pets || form.pets.length === 0) {
        throw new Error('Pet information is required to fetch available time slots');
      }
      const data = await fetchAvailableTimeSlots(form.location.clinicId, providerId, date, form.pets);

      // Handle different response formats
      if (providerId === 'no_preference' && data && typeof data === 'object') {
        // The concrete vet for each slot is resolved when the user picks a time
        // (see slotVetByTime below), so the booking submits a real vet, not 'no_preference'.
        setSlotVetByTime(data.slotVetByTime || {});
        // Set time slots only if we actually fetched them (date was provided)
        setTimeSlots(date ? data.timeSlots : []);
      } else {
        // For specific vets, data is just the array of time slots
        setSlotVetByTime({});
        setTimeSlots(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error loading time slots:', err);
      setError(err.message || 'Unable to load available times. Please try again.');
      setTimeSlots([]);
    } finally {
      setLoadingTimeSlots(false);
    }
  };

  return (
    <Box maxW="100%" overflow="hidden">
      <Heading size="md" mb={{ base: 4, md: 6 }}>Select Provider & Schedule</Heading>
      
      {/* Emergency Contact Alert */}
      <Alert status="error" mb="4" borderRadius="8px" bg="#FEE2E2" borderColor="#FCA5A5" borderWidth="1px">
        <AlertIcon color="#DC2626" />
        <Box minW={0} overflowWrap="anywhere">
          {form.location?.emergencyMessage ? (
            // Clinic-configured message fully replaces the default line.
            <Text fontSize="sm" color="#991B1B" wordBreak="break-word" whiteSpace="pre-line">
              {form.location.emergencyMessage}
            </Text>
          ) : (
            // Default emergency line, shown whenever no custom message is configured.
            <Text fontSize="sm" color="#991B1B" wordBreak="break-word">
              <Text as="span" fontWeight="600">For emergencies please</Text>
              {form.location?.phone ? (
                <>
                  {' '}Call the clinic on:{' '}
                  <Text as="span" fontWeight="700">{form.location.phone}</Text>
                </>
              ) : (
                ' contact the clinic directly.'
              )}
            </Text>
          )}
        </Box>
      </Alert>

      {/* Info Alert */}
      <Alert status="info" mb="4" borderRadius="8px" bg="#FEF3C7" borderColor="#FDE68A" borderWidth="1px">
        <AlertIcon color="#92400E" />
        <Box>
          <Text fontSize="sm" color="#78350F">
            If your preferred time is not available, please contact the clinic.
          </Text>
        </Box>
      </Alert>
      
      {error && (
        <Alert status="warning" mb="4" borderRadius="8px">
          <AlertIcon />
          {error}
        </Alert>
      )}

      {/* Provider Selection — hidden when the clinic disables showing the vet.
          The default provider ('no_preference', set in the mount effect above) still
          applies, so the backend auto-assigns a vet per slot and booking works. */}
      {showVet && (
      <Box mb="8">
        <Text fontWeight="600" fontSize="lg" mb="3">Choose Your Vet</Text>
        {loadingProviders ? (
          <Box textAlign="center" py="6">
            <Spinner color="#04385D" />
            <Text mt="2" color="gray.600" fontSize="sm">Loading providers...</Text>
          </Box>
        ) : providers.length === 0 ? (
          <Box textAlign="center" py="6">
            <Text color="gray.600">No providers available for this location.</Text>
          </Box>
        ) : (
          <Select
            w="100%"
            maxW="100%"
            placeholder="Select a vet"
            value={form.provider?.id || (form.provider?.name === 'no_preference' ? 'no_preference' : '')}
            onChange={(e) => {
              const selectedValue = e.target.value;
              if (selectedValue === 'no_preference') {
                setForm((f) => ({
                  ...f,
                  provider: { id: 'no_preference', name: 'No Preference' },
                  assignedVet: null, // Clear any previously assigned vet
                  date: '',
                  time: '',
                }));
              } else {
                const selectedProvider = providers.find(p => p.id === selectedValue);
                setForm((f) => ({
                  ...f,
                  provider: selectedProvider || null,
                  assignedVet: null, // Clear assigned vet when specific vet is selected
                  date: '',
                  time: '',
                }));
              }
            }}
            bg="white"
            borderColor="gray.300"
            _hover={{ borderColor: "#04385D" }}
            _focus={{ borderColor: "#04385D", boxShadow: "0 0 0 1px #04385D" }}
          >
            <option value="no_preference">No Preference</option>
            {providers.map((p, index) => {
              const providerId = p.id || `temp-${index}`;
              // const subtitle = p.specialty || 
              //                  (p.roles && p.roles.length > 0 ? p.roles.join(', ') : '') ||
              //                  (p.vetRoles && p.vetRoles.length > 0 ? p.vetRoles.join(', ') : '');
              const displayName = p.name;
              
              return (
                <option key={providerId} value={providerId}>
                  {displayName}
                </option>
              );
            })}
          </Select>
        )}
      </Box>
      )}

      {/* Date & Time Selection - Only show when provider is selected */}
      {form.provider && (
        <Box>
          <Text fontWeight="600" fontSize="lg" mb="3">Select Date & Time</Text>
          <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={{ base: 4, md: 5 }}>
            <GridItem>
              <FormControl isRequired isInvalid={showErrors && !form.date}>
                <FormLabel fontWeight="600" mb="2">
                  Select Date
                </FormLabel>
                <Input
                  type="date"
                  min={minDate}
                  max={maxDate}
                  value={form.date}
                  onChange={(e) => setForm((f) => ({ ...f, date: e.target.value, time: '' }))}
                />
                <FormErrorMessage>Please select a date</FormErrorMessage>
              </FormControl>
            </GridItem>
            <GridItem>
              <Text fontWeight="600" mb="2">Available Times</Text>
              {!form.date ? (
                <Text color="gray.500">Please select a date first</Text>
              ) : loadingTimeSlots ? (
                <Box>
                  <Spinner size="sm" color="#04385D" />
                  <Text ml="2" display="inline" color="gray.600" fontSize="sm">
                    Loading available times...
                  </Text>
                </Box>
              ) : timeSlots.length === 0 ? (
                <Text color="orange.600">No time slots available for this date</Text>
              ) : (
                <Stack direction="row" wrap="wrap" spacing="8px">
                  {timeSlots.map((t) => {
                    const selected = form.time === t;
                    return (
                      <Button
                        key={t}
                        size="md"
                        minH="44px"
                        px={{ base: 3, md: 4 }}
                        variant={selected ? 'solid' : 'outline'}
                        bg={selected ? '#04385D' : 'transparent'}
                        color={selected ? 'white' : '#04385D'}
                        borderColor="#04385D"
                        borderWidth={selected ? 0 : '1px'}
                        _hover={{ bg: selected ? '#032C47' : 'rgba(4,56,93,0.08)' }}
                        onClick={() => setForm((f) => ({
                          ...f,
                          time: t,
                          // For "no preference", lock in the vet the backend assigned to this slot.
                          ...(f.provider?.id === 'no_preference' ? { assignedVet: slotVetByTime[t] || null } : {}),
                        }))}
                      >
                        {t}
                      </Button>
                    );
                  })}
                </Stack>
              )}
              {showErrors && form.date && !form.time && timeSlots.length > 0 && !loadingTimeSlots && (
                <Text color="red.500" fontSize="sm" mt="2">
                  Please select a time slot
                </Text>
              )}
            </GridItem>
          </Grid>
        </Box>
      )}
    </Box>
  );
}

export default Step3ProviderTime;

